// Plan catalog of the signup redesign: Basic (monthly only), Unlimited and Teams, each with
// monthly and yearly codes/prices taken from the existing service_package_constants so
// coupons and checkout keep speaking the same codes. A backend catalog (GET /api/plans/,
// M3) can override prices by id; this file is the fallback and the source of the copy.
import servicePackages from '@/components/service_package_constants'

function byQueryValue (value) {
  return Object.values(servicePackages).find((p) => p.queryParam && p.queryParam.value === value) || null
}

const BASIC = byQueryValue('basic')
const UNLIMITED_MONTH = byQueryValue('unlimited')
const UNLIMITED_YEAR = byQueryValue('yearlyunlimited')
const TEAMS_MONTH = byQueryValue('monthy-team-plan')
const TEAMS_YEAR = byQueryValue('yearly-team-plan')

export const SIGNUP_PLANS = [
  {
    id: 'basic',
    title: 'Basic',
    tag: 'For those getting started',
    codes: { month: BASIC && BASIC.code, year: null },
    prices: { month: BASIC ? BASIC.price : 40, year: null },
    credits: '20 credits / month',
    creditLine: '',
    bullets: ['POP Rank Engine', 'Unlimited domains', '20 POP EEAT, NLP, Watchdog, and AI credits', 'POP AI Writer',
      'POP AI Visibility Tracker'],
  },
  {
    id: 'unlimited',
    title: 'Unlimited',
    tag: 'Best for professionals',
    recommended: true,
    codes: { month: UNLIMITED_MONTH && UNLIMITED_MONTH.code, year: UNLIMITED_YEAR && UNLIMITED_YEAR.code },
    prices: { month: UNLIMITED_MONTH ? UNLIMITED_MONTH.price : 72, year: UNLIMITED_YEAR ? UNLIMITED_YEAR.price : 739 },
    credits: 'Unlimited POP reports',
    creditLine: 'POP and Watchdog reports are unlimited. 50 credits cover EEAT, NLP, Watchdog and AI.',
    bullets: ['POP Rank Engine', 'Unlimited domains', 'Unlimited POP reports and Watchdog reports per month*',
      '50 POP EEAT, NLP, Watchdog, and AI credits', 'POP AI Writer', 'POP AI Visibility Tracker', 'Keyword tools'],
  },
  {
    id: 'teams',
    title: 'Teams',
    tag: 'Best for teams',
    codes: { month: TEAMS_MONTH && TEAMS_MONTH.code, year: TEAMS_YEAR && TEAMS_YEAR.code },
    prices: { month: TEAMS_MONTH ? TEAMS_MONTH.price : 143, year: TEAMS_YEAR ? TEAMS_YEAR.price : 1425 },
    credits: 'Unlimited POP reports, 5 sub-accounts',
    creditLine: 'POP and Watchdog reports are unlimited. 120 credits cover EEAT, NLP, Watchdog and AI.',
    bullets: ['POP Rank Engine', 'Unlimited domains', 'Unlimited POP reports and Watchdog reports per month*',
      '120 POP EEAT, NLP, Watchdog, and AI credits', 'POP AI Writer', 'POP AI Visibility Tracker', 'Keyword tools',
      'Starts at 5 sub-accounts (max 100)', 'Extra seats $12/mo or $120/yr'],
  },
]

// ?product= values the pricing page and marketing links send today (service_package_constants
// queryParam values) plus the legacy ones that still arrive, mapped to a card + interval.
const PRODUCT_PARAM_MAP = {
  basic: ['basic', 'month'],
  unlimited: ['unlimited', 'month'],
  yearlyunlimited: ['unlimited', 'year'],
  'monthy-team-plan': ['teams', 'month'],
  'yearly-team-plan': ['teams', 'year'],
  premium: ['unlimited', 'month'],
  agency_5_monthly: ['teams', 'month'],
  agency_10_monthly: ['teams', 'month'],
  agency_20_monthly: ['teams', 'month'],
  agency_5_yearly: ['teams', 'year'],
  agency_10_yearly: ['teams', 'year'],
  agency_20_yearly: ['teams', 'year'],
}

/** Merge GET /api/plans/ over the local cards (prices and copy win, ids must match). */
export function applyCatalog (catalog) {
  const remote = (catalog && catalog.plans) || []
  if (!remote.length) return SIGNUP_PLANS
  return SIGNUP_PLANS.map((local) => {
    const incoming = remote.find((p) => p.id === local.id)
    if (!incoming) return local
    return {
      ...local,
      ...incoming,
      codes: { ...local.codes, ...(incoming.codes || {}) },
      prices: {
        month: incoming.prices && incoming.prices.month != null ? Number(incoming.prices.month) : local.prices.month,
        year: incoming.prices && incoming.prices.year != null ? Number(incoming.prices.year) : local.prices.year,
      },
      bullets: incoming.bullets && incoming.bullets.length ? incoming.bullets : local.bullets,
    }
  })
}

export function round2 (n) {
  return Math.round(Number(n) * 100) / 100
}

/** Whole dollars like the pricing page ($40, $739, $1,425); cents only when there are some ($14.40). */
export function formatPrice (n) {
  const value = Number(n || 0)
  const digits = Number.isInteger(value) ? 0 : 2
  return '$' + value.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits })
}

export function planById (id) {
  return SIGNUP_PLANS.find((p) => p.id === id) || null
}

export function priceFor (plan, interval) {
  if (!plan) return 0
  return interval === 'year' && plan.prices.year ? plan.prices.year : plan.prices.month
}

/** What the card shows per month: the yearly price / 12, rounded to whole dollars, for yearly plans. */
export function perMonth (plan, interval) {
  if (!plan) return 0
  return interval === 'year' && plan.prices.year ? Math.round(plan.prices.year / 12) : plan.prices.month
}

export function yearlySaving (plan) {
  if (!plan || !plan.prices.year) return 0
  return round2(plan.prices.month * 12 - plan.prices.year)
}

/** { plan, interval } for a ?product= value, or null when unknown. */
export function mapProductParam (product) {
  const entry = PRODUCT_PARAM_MAP[String(product || '').toLowerCase()]
  if (!entry) return null
  const plan = planById(entry[0])
  const interval = entry[1] === 'year' && plan && plan.codes.year ? 'year' : 'month'
  return plan ? { plan, interval } : null
}

/** The compact object stored in the signup context and shown by PlanSummaryCard. */
export function selectedPlan (plan, interval) {
  if (!plan) return null
  const useYear = interval === 'year' && plan.codes.year
  return {
    id: plan.id,
    title: plan.title,
    code: useYear ? plan.codes.year : plan.codes.month,
    interval: useYear ? 'year' : 'month',
    price: priceFor(plan, useYear ? 'year' : 'month'),
  }
}

/** Every selectable (plan, interval) pair for the inline switcher on the signup page. */
export function planOptions () {
  const options = []
  SIGNUP_PLANS.forEach((plan) => {
    options.push({ value: `${plan.id}:month`, label: `${plan.title}, ${formatPrice(plan.prices.month)} / month`, plan, interval: 'month' })
    if (plan.codes.year) {
      options.push({ value: `${plan.id}:year`, label: `${plan.title}, ${formatPrice(plan.prices.year)} / year`, plan, interval: 'year' })
    }
  })
  return options
}
